import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Procurement Solutions",
  "Supply Solutions",
  "Interior Design",
  "Project Execution",
  "Business Support",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service_required: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please share your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", company: "", email: "", phone: "", service_required: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[var(--nv-paper)] text-[var(--nv-navy)] py-28 md:py-40 overflow-hidden"
    >
      <Toaster position="top-center" richColors />

      <ContactBlueprintLayer />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 items-end mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-8">
            <div className="overline text-[var(--nv-gold)] flex items-center gap-3">
              <span className="inline-block w-6 h-px bg-[var(--nv-gold)]" />
              <span>Contact — Start a Conversation</span>
            </div>
            <h2 className="mt-6 display text-5xl md:text-7xl lg:text-8xl" data-testid="contact-heading">
              Let&apos;s build something <span className="italic text-[var(--nv-gold)]">reliable.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-10 lg:gap-16">
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8 }}
            className="col-span-12 lg:col-span-7 space-y-8"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field label="Name" required value={form.name} onChange={onChange("name")} testId="contact-input-name" />
              <Field label="Company" value={form.company} onChange={onChange("company")} testId="contact-input-company" />
              <Field label="Email" required type="email" value={form.email} onChange={onChange("email")} testId="contact-input-email" />
              <Field label="Phone" type="tel" value={form.phone} onChange={onChange("phone")} testId="contact-input-phone" />
            </div>

            <div>
              <label className="overline text-[var(--nv-navy)]/60 block mb-3">Service Required</label>
              <div className="flex flex-wrap gap-2" data-testid="contact-service-chips">
                {services.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, service_required: s }))}
                    className={`px-4 py-2 rounded-full text-[12px] font-mono tracking-[0.16em] uppercase border transition-colors duration-300 ${
                      form.service_required === s
                        ? "bg-[var(--nv-navy)] text-[var(--nv-paper)] border-[var(--nv-navy)]"
                        : "bg-transparent text-[var(--nv-navy)]/80 border-[var(--nv-navy)]/25 hover:border-[var(--nv-navy)]"
                    }`}
                    data-testid={`contact-service-${s.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="overline text-[var(--nv-navy)]/60 block mb-3">Message *</label>
              <textarea
                required
                value={form.message}
                onChange={onChange("message")}
                rows={5}
                placeholder="Tell us about your project, timelines or the outcome you're after."
                className="w-full bg-transparent border-b border-[var(--nv-navy)]/25 focus:border-[var(--nv-navy)] font-sans text-lg py-3 outline-none resize-none placeholder:text-[var(--nv-navy)]/35"
                data-testid="contact-input-message"
              />
            </div>

            <div className="flex flex-wrap items-center gap-6 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="contact-submit"
              >
                {loading ? "Sending…" : sent ? "Sent — send another?" : "Send Enquiry"}
                <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
              <p className="text-xs text-[var(--nv-navy)]/50 max-w-sm">
                We typically respond within one business day.
              </p>
            </div>
          </motion.form>

          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="col-span-12 lg:col-span-5 lg:pl-8 lg:border-l lg:border-[var(--nv-line)]"
          >
            <div className="space-y-8">
              <ContactRow
                icon={Mail}
                label="Email"
                value="nsoulventures1@gmail.com"
                href="mailto:nsoulventures1@gmail.com"
                testId="contact-info-email"
              />
              <ContactRow
                icon={Phone}
                label="Phone"
                value="+91 89780 42358"
                href="tel:+918978042358"
                testId="contact-info-phone"
              />
              <ContactRow
                icon={MapPin}
                label="Office"
                value="India — visits by appointment"
                testId="contact-info-address"
              />
            </div>

            <div
              className="mt-12 relative aspect-[4/3] w-full overflow-hidden border border-[var(--nv-line)]"
              data-testid="contact-map-placeholder"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[var(--nv-navy)]/6 via-white to-[var(--nv-gold)]/8" />
              <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 400 300" fill="none">
                <defs>
                  <pattern id="grid" width="30" height="30" patternUnits="userSpaceOnUse">
                    <path d="M 30 0 L 0 0 0 30" stroke="rgba(10,25,47,0.16)" strokeWidth="0.5" fill="none" />
                  </pattern>
                </defs>
                <rect width="400" height="300" fill="url(#grid)" />
                <circle cx="200" cy="150" r="8" fill="#C5A059" />
                <circle cx="200" cy="150" r="18" fill="none" stroke="#C5A059" strokeOpacity="0.5" />
                <circle cx="200" cy="150" r="30" fill="none" stroke="#C5A059" strokeOpacity="0.25" />
              </svg>
              <div className="absolute bottom-4 left-4 overline text-[var(--nv-navy)]/70">
                Map placeholder — Google Map embed
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", required = false, testId }) {
  return (
    <div>
      <label className="overline text-[var(--nv-navy)]/60 block mb-3">
        {label}{required ? " *" : ""}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full bg-transparent border-b border-[var(--nv-navy)]/25 focus:border-[var(--nv-navy)] font-sans text-lg py-3 outline-none placeholder:text-[var(--nv-navy)]/35"
      />
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, testId }) {
  const inner = (
    <div className="flex items-start gap-5 group">
      <span className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-[var(--nv-navy)]/25 group-hover:border-[var(--nv-gold)] transition-colors">
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <div className="overline text-[var(--nv-navy)]/60">{label}</div>
        <div className="font-serif text-xl md:text-2xl mt-1 tracking-tight">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testId} className="block">{inner}</a>
  ) : (
    <div data-testid={testId}>{inner}</div>
  );
}


/* Contact blueprint layer — final chapter of the drafting vocabulary,
   quieter than earlier sections to signal a conclusion. */
function ContactBlueprintLayer() {
  return (
    <div className="absolute inset-0 pointer-events-none z-0" aria-hidden>
      <svg
        viewBox="0 0 1600 2200"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full opacity-[0.09]"
      >
        <defs>
          <pattern id="ct-grid-fine" x="0" y="0" width="12" height="12" patternUnits="userSpaceOnUse">
            <path d="M 12 0 L 0 0 0 12" fill="none" stroke="#0A192F" strokeWidth="0.35" />
          </pattern>
          <pattern id="ct-grid" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#0A192F" strokeWidth="0.55" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#ct-grid-fine)" />
        <rect width="100%" height="100%" fill="url(#ct-grid)" />

        {/* Closing compass — echoes the emblem, marks the arrival */}
        <g stroke="#0A192F" strokeWidth="0.7" fill="none">
          <circle cx="1400" cy="440" r="240" strokeDasharray="2 6" />
          <circle cx="1400" cy="440" r="160" />
          <circle cx="1400" cy="440" r="90" strokeDasharray="1 5" />
          <line x1="1400" y1="140" x2="1400" y2="740" strokeDasharray="1 5" />
          <line x1="1100" y1="440" x2="1700" y2="440" strokeDasharray="1 5" />
          <circle cx="1400" cy="440" r="4" fill="#C5A059" stroke="none" />
        </g>

        <g stroke="#0A192F" strokeWidth="0.7" fill="none">
          <path d="M 140 1520 L 300 1440 L 460 1520 L 460 1680 L 300 1760 L 140 1680 Z" />
          <path d="M 300 1440 L 300 1600 L 460 1680" />
          <path d="M 300 1600 L 140 1520" />
        </g>

        <g stroke="#0A192F" strokeWidth="0.6" fill="none">
          <path d="M 30 30 L 62 30 M 30 30 L 30 62" />
          <path d="M 1570 30 L 1538 30 M 1570 30 L 1570 62" />
          <path d="M 30 2170 L 62 2170 M 30 2170 L 30 2138" />
          <path d="M 1570 2170 L 1538 2170 M 1570 2170 L 1570 2138" />
        </g>

        <g fill="#C5A059">
          <circle cx="360" cy="220" r="2.6" />
          <circle cx="1200" cy="1080" r="2.6" />
          <circle cx="840" cy="1900" r="2.6" />
        </g>

        <g
          fontFamily="'JetBrains Mono', ui-monospace, monospace"
          fill="#0A192F"
          fillOpacity="0.55"
          fontSize="10"
          letterSpacing="0.22em"
        >
          <text x="30" y="80">NV — 07 · CONTACT</text>
          <text x="1570" y="80" textAnchor="end">SCALE 1:1</text>
          <text x="30" y="2130">END OF JOURNEY</text>
          <text x="1570" y="2130" textAnchor="end">SHEET 07/07</text>
        </g>
      </svg>
    </div>
  );
}
